import { useQuery } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';

import { RMPServiceCode } from '@/shared/types/serviceCodes';
import {
  FormDatePicker,
  FormTextInput,
  FormSelect,
  StepControlPanel,
  UKRAINE_PHONE_MASK_START,
  Grid,
} from '@/shared/ui';
import { MultipleFormInputPhone } from '@/shared/ui/InputPhone/MultipleFormInputPhone';

import { FormConstants, genders, IdConstants } from '@/components/Forms/constants';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import { formatDate } from '@/helpers/dates';

import ServiceAvailabilityContext, { Reason } from '@/contexts/ServiceAvailability/ServiceAvailabilityContext';

import { rmp } from '@/api/integrations/rmp';
import { queryKeys } from '@/api/queryKeys';
import { DocumentTypeCode } from '@/api/types';

import api from '@/api';

import {
  MISSING_PERSON_STATUS_OPTIONS,
  missingPersonAllowedFileTypes,
  MissingPersonStatus,
  YesNoSelectOptions,
} from '../../lib/constants';
import { MissingPersonFormData } from '../../model/types';

export const MissingPersonPersonalData: React.FC<StepProps> = ({
  next,
  serviceCode,
  currentRequestId,
  onStepDraftSave,
}) => {
  const { disableCause } = useContext(ServiceAvailabilityContext);

  const { control, getValues, setValue, watch, formState } = useFormContext<MissingPersonFormData>();

  const { data: optionsCountry = [], isLoading: isLoadingCountry } = useQuery({
    queryKey: queryKeys.dictionary.countries(),
    queryFn: () => api.dictionary.getCountry().then((r) => r.data.map((c) => ({ id: c.id, label: c.shortName }))),
  });

  const { data: militaryServiceType = [], isLoading: isLoadingMilitary } = useQuery({
    queryKey: queryKeys.rmp.militaryServiceTypes(),
    queryFn: () =>
      rmp.dictionary.getMilitaryServiceTypes().then((r) => r.data.map((t) => ({ id: t.id, label: t.name }))),
  });

  const { data: disappearanceDocs = [] } = useERZDocumentTypes({
    allowedDocumentTypeCodes: [
      DocumentTypeCode.DISAPPEARANCE_MISSING_PERSON,
      DocumentTypeCode.MISSING_PERSON_PHOTO_CARD,
    ],
  });
  const disappearanceDocType = disappearanceDocs.find((d) => d.code === DocumentTypeCode.DISAPPEARANCE_MISSING_PERSON);
  const disappearancePhotoCardDocType = disappearanceDocs.find(
    (d) => d.code === DocumentTypeCode.MISSING_PERSON_PHOTO_CARD,
  );

  const phones = getValues('missingPerson.phones');
  const missingPersonStatus = watch('missingPerson.person_status');
  const withoutCitizenship = watch('missingPerson.without_citizenship');
  const availableGenders = genders.filter(({ id }) => id !== 'U');

  useEffect(() => {
    if (isLoadingCountry || isLoadingMilitary) return;

    const formValues = getValues();

    isEmpty(formValues.missingPerson?.person_status) &&
      setValue('missingPerson.person_status', { id: MissingPersonStatus.military, label: 'Військовий' });

    isEmpty(formValues.missingPerson?.without_citizenship) &&
      setValue('missingPerson.without_citizenship', YesNoSelectOptions.NO);

    const ukraine = optionsCountry.find(({ id }) => id === IdConstants.UKRAINE);
    isEmpty(formValues.missingPerson?.citizenship) && setValue('missingPerson.citizenship', ukraine);
  }, [isLoadingCountry, isLoadingMilitary]);

  const checkBeforeStepChange = async (): Promise<boolean> => {
    const formData = getValues();

    const isApplicantionUnique = (
      await api.services.verifySubmitAvailability(serviceCode as RMPServiceCode, {
        first_name: formData.missingPerson.first_name,
        last_name: formData.missingPerson.last_name,
        patronymic: formData.missingPerson.patronymic,
        birthday: formData.missingPerson.birthday,
      })
    ).data.canSubmit;

    if (!isApplicantionUnique) {
      disableCause({
        reason: Reason.DUPLICATED_APPLICATION,
        message: (
          <div>
            <p>
              На жаль, протягом доби ви можете подати лише одну анкету про зниклу особу безвісти за особливих обставин
              до уповноваженого з питань осіб зниклих безвісти за особливих обставин.
            </p>
          </div>
        ),
      });
    }

    return isApplicantionUnique;
  };

  const isMissingPersonStatusMilitary = missingPersonStatus?.id === MissingPersonStatus.military;
  const isWithoutCitizenship = withoutCitizenship?.id === YesNoSelectOptions.NO.id;

  return (
    <>
      <FormBlock title="Особисті дані зниклої особи">
        <FormTextInput
          name="missingPerson.last_name"
          control={control}
          label="Прізвище"
          required
          maxLength={FormConstants.INPUT_MAX_LENGTH_240}
        />
        <FormTextInput
          name="missingPerson.first_name"
          control={control}
          label="Ім'я"
          required
          maxLength={FormConstants.INPUT_MAX_LENGTH_240}
        />
        <FormTextInput
          name="missingPerson.patronymic"
          control={control}
          label="По батькові"
          maxLength={FormConstants.INPUT_MAX_LENGTH_240}
        />
        <FormDatePicker maxDateToday name="missingPerson.birthday" control={control} label="Дата народження" required />
        <FormSelect name="missingPerson.sex" control={control} label="Стать" required options={availableGenders} />
        <FormSelect
          name="missingPerson.without_citizenship"
          control={control}
          label="Особа без громадянства"
          required
          options={[YesNoSelectOptions.YES, YesNoSelectOptions.NO]}
          onChange={(value: string) =>
            setValue('missingPerson.citizenship', (Number(value) ? { id: null, label: null } : null) as any)
          }
        />
        {isWithoutCitizenship && (
          <FormSelect
            name="missingPerson.citizenship"
            control={control}
            options={optionsCountry}
            label="Громадянство"
            required
          />
        )}
        <FormTextInput
          name="missingPerson.birth_place"
          control={control}
          label="Місце народження"
          required
          maxLength={FormConstants.INPUT_MAX_LENGTH}
        />
        <FormTextInput
          name="missingPerson.rnokpp"
          mask="9999999999"
          maxLength={10}
          control={control}
          label="Ідентифікаційний код (РНОКПП)"
        />

        <DocumentsData
          withUploader
          documentTypeSelectHidden
          maxDocumentsCount={1}
          maxFileCount={1}
          fileRequired={false}
          name="missingPerson.document_photo"
          documentUploadLabel="Фото зниклого(Рекомендуємо додати фото в форматі 3,5*4.5)"
          request_id={currentRequestId ?? ''}
          documentTypeEntities={disappearancePhotoCardDocType ? [disappearancePhotoCardDocType] : []}
          onSaveDraft={onStepDraftSave}
          allowedFileTypes={missingPersonAllowedFileTypes}
        />

        <FormSelect
          name="missingPerson.person_status"
          control={control}
          label="Статус"
          required
          options={MISSING_PERSON_STATUS_OPTIONS}
        />
        {isMissingPersonStatusMilitary && (
          <>
            <FormTextInput
              name="missingPerson.military_service_category"
              control={control}
              label="Військомат з якого був призваний (назва ТЦК)"
              required
              maxLength={FormConstants.INPUT_MAX_LENGTH_512}
            />
            <FormSelect
              name="missingPerson.military_service_type"
              control={control}
              options={militaryServiceType}
              label="Вид служби"
              required
            />
            <FormTextInput
              name="missingPerson.military_unit"
              control={control}
              label="Військова частина (номер)"
              required
              maxLength={FormConstants.INPUT_MAX_LENGTH_512}
            />
            <FormTextInput
              name="missingPerson.military_brigade"
              control={control}
              label="Бригада"
              required
              maxLength={FormConstants.INPUT_MAX_LENGTH_512}
            />
            <FormTextInput
              name="missingPerson.military_formation"
              control={control}
              label="Батальйон, рота, взвод"
              required
              maxLength={FormConstants.INPUT_MAX_LENGTH_512}
            />
            <FormTextInput
              name="missingPerson.military_rank"
              control={control}
              label="Звання, посада зниклого"
              required
              maxLength={FormConstants.INPUT_MAX_LENGTH_512}
            />
            <FormTextInput
              name="missingPerson.military_call_sign"
              control={control}
              label="Позивний"
              required
              maxLength={FormConstants.INPUT_MAX_LENGTH_100}
            />
          </>
        )}
        <MultipleFormInputPhone
          onChange={(newPhones) => newPhones && setValue('missingPerson.phones', newPhones)}
          value={phones}
          onlyCountries={[]}
          errors={formState.errors.missingPerson?.phones as Array<{ message: string }>}
          maxPhoneNumbersQuantity={4}
        />

        <DocumentsData
          withUploader
          documentTypeSelectHidden
          maxDocumentsCount={1}
          maxFileCount={5}
          fileRequired={false}
          name="missingPerson.document_proofs_of_missings"
          documentUploadLabel="Сповіщення від ТЦК, або документи які засвідчують факт зникнення особи або перебування в полоні"
          request_id={currentRequestId ?? ''}
          documentTypeEntities={disappearanceDocType ? [disappearanceDocType] : []}
          onSaveDraft={onStepDraftSave}
          allowedFileTypes={missingPersonAllowedFileTypes}
        />
      </FormBlock>

      <StepControlPanel next={() => next(checkBeforeStepChange)} />
    </>
  );
};

export const MissingPersonPersonalDataPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonFormData>();
  const personalDataValues = getValues('missingPerson');

  const items: Array<[string, Nullable<string>]> = [
    ['Прізвище', personalDataValues?.last_name],
    ['Ім’я', personalDataValues?.first_name],
    ['По батькові', personalDataValues?.patronymic],
    ['Дата народження', formatDate(personalDataValues?.birthday)],
    ['Стать', personalDataValues?.sex?.label],
    ['Особа без громадянства', personalDataValues?.without_citizenship?.label],
    ['Громадянство', personalDataValues?.citizenship?.label],
    ['Місце народження', personalDataValues?.birth_place],
    ['Ідентифікаційний код (РНОКПП)', personalDataValues?.rnokpp],
    [
      'Фото зниклого(Рекомендуємо додати фото в формті 3,5*4.5)',
      personalDataValues?.document_photo?.[0]?.attachments?.file_name ??
        personalDataValues?.document_photo?.[0]?.attachments?.fileName ??
        null,
    ],
    [
      'Номер телефона',
      personalDataValues?.phones
        ?.filter((phone) => phone !== UKRAINE_PHONE_MASK_START)
        .map((phone) => phone)
        .join(', '),
    ],
    ['Статус', personalDataValues?.person_status?.label],
  ];

  if (personalDataValues?.person_status?.id === MissingPersonStatus.military) {
    items.push(
      ['Військомат з якого був призваний (назва ТЦК)', personalDataValues?.military_service_category],
      ['Вид служби', personalDataValues?.military_service_type?.label],
      ['Військова частина (номер)', personalDataValues?.military_unit],
      ['Бригада', personalDataValues?.military_brigade],
      ['Батальйон, рота, взвод', personalDataValues?.military_formation],
      ['Звання, посада зниклого', personalDataValues?.military_rank],
      ['Позивний', personalDataValues?.military_call_sign],
      [
        'Сповіщення від ТЦК, або документи які засвідчують факт зникнення особи або перебування в полоні',
        personalDataValues?.document_proofs_of_missings?.[0]?.attachments?.fileNames
          ? Object.values(personalDataValues?.document_proofs_of_missings?.[0]?.attachments?.fileNames).join(', ')
          : (personalDataValues?.document_proofs_of_missings?.[0]?.attachments?.file_name ??
            personalDataValues?.document_proofs_of_missings?.[0]?.attachments?.fileName ??
            null),
      ],
    );
  }

  return <Grid title="Особисті дані зниклої особи" data={items} />;
};
